import {Context} from '@opentelemetry/api'
import {ReadableSpan, Span, SpanProcessor} from '@opentelemetry/sdk-trace-web'
import {getSessionUser} from 'app/features/auth/redux/AuthCRUD'

export class SessionIdProcessor implements SpanProcessor {
  forceFlush(): Promise<void> {
    return Promise.resolve()
  }

  /**
   * Detect browser name based on user agent
   */ private getBrowserName(): string | null {
    try {
      const ua = navigator.userAgent

      // Use a simplified approach to reduce cognitive complexity
      const browserMap = [
        {test: /firefox/i, name: 'Firefox'},
        {test: /edg/i, name: 'Edge'},
        {test: /chrome|chromium/i, name: 'Chrome'},
        {test: /safari/i, name: 'Safari'},
        {test: /opera|opr/i, name: 'Opera'},
        {test: /trident|msie/i, name: 'Internet Explorer'},
      ]

      for (const browser of browserMap) {
        if (browser.test.test(ua)) {
          return browser.name
        }
      }
      return null
    } catch (e) {
      return null
    }
  }
  /**
   * Returns detailed device information including type, orientation,
   * pixel ratio, and touch capability
   */
  private getDeviceInfo(): string {
    try {
      const ua = navigator.userAgent.toLowerCase()

      // Check for mobile-specific keywords in user agent
      const isMobileByAgent = /(android|webos|iphone|ipod|blackberry|windows phone)/i.test(ua)
      const isTabletByAgent = /ipad|tablet/i.test(ua)

      // Get device dimensions
      const width = window.screen.width
      const height = window.screen.height
      const minDimension = Math.min(width, height)
      const maxDimension = Math.max(width, height)

      // Check touch capability
      const touchPoints = navigator.maxTouchPoints || 0
      const isTouchDevice = 'ontouchstart' in window || touchPoints > 0

      // Tablet detection - either declared as tablet in UA or has tablet-like dimensions
      if (isTabletByAgent || (isTouchDevice && minDimension >= 600 && maxDimension <= 1366)) {
        return 'tablet'
      }
      // Phone detection - small touch screen or mobile UA
      else if ((isMobileByAgent && !isTabletByAgent) || (isTouchDevice && minDimension < 600)) {
        return 'phone'
      }
      // Everything else is laptop/desktop
      return 'laptop'
    } catch (e) {
      return 'laptop'
    }
  }

  /**
   * Extract browser version from user agent
   */ private getBrowserVersion(): string | null {
    try {
      const ua = navigator.userAgent

      // Simple regex to extract versions for common browsers
      const versionPatterns = [
        /(?:firefox|fxios)\/(\d+)/i, // Firefox
        /(?:chrome|crios)\/(\d+)/i, // Chrome
        /(?:edg)\/(\d+)/i, // Edge
        /(?:opr|opera)\/(\d+)/i, // Opera
        /version\/(\d+).*safari/i, // Safari
      ]

      for (const pattern of versionPatterns) {
        const match = pattern.exec(ua)
        if (match?.[1]) {
          return match[1]
        }
      }
      return null
    } catch (e) {
      return null
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onStart(span: Span, parentContext: Context): void {
    // Add browser information
    const browserName = this.getBrowserName()
    if (browserName) {
      span.setAttribute('browser.name', browserName)
    }
    const browserVersion = this.getBrowserVersion()
    if (browserVersion) {
      span.setAttribute('browser.version', browserVersion)
    }
    // Add device type information
    span.setAttribute('device.type', this.getDeviceInfo())

    // Add page URL information
    span.setAttribute('app.url', window.location.href)

    // Add user information
    const user = getSessionUser()
    if (user) {
      span.setAttribute('app.session.name', `${user.firstName} ${user.lastName}`)
      if (user.store) {
        span.setAttribute('app.session.storeName', user.store.name)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  onEnd(span: ReadableSpan): void {}

  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}
